import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  listPromptsAction,
  listLlms,
  listPromptScreens,
} from '../../../../../store/actions/promptsv2.action';
import { getAllConditionListAction } from '../../../../../store/actions/condition.action';
import { getAllSpecialtyListAction } from '../../../../../store/actions/specialty.action';
import { Dropdown, Menu } from 'antd';
import { PlusIcon, ActionMenuIcon, EditAppointmentIcon } from 'src/icons';
import { Button } from '../../../../baseComponents';
import { SuperAdminTable } from '../../../../baseComponents/Table';
import ConditionsPromptsFilters from './ConditionsPromptsFilters';
import EditPromptModal from './EditPromptModal';
import CreatePromptModal from './CreatePromptModal';
import { getLlmDisplayComponent } from 'src/utils/llmUtils';

const ConditionsPrompts = () => {
  const dispatch = useDispatch();
  const { prompts, promptsLoading, llms, llmsLoading, promptScreensLoading } =
    useSelector((state) => state?.promptsState);
  const { conditionsLoading, specialties, activeSubTab } = useSelector(
    (state) => ({
      conditions: state?.conditionState?.conditionsDropdown,
      conditionsLoading: state?.conditionState?.getAllConditionsListLoading,
      specialties: state?.specialtyState?.getAllSpecialtiesList,
      specialtiesLoading: state?.conditionState?.isLoading,
      currentPractitioner: state?.practitionerState?.currentPractitioner,
      serviceRequestTypes: state?.serviceRequestState?.srTypeList,
      activeSubTab: state?.superadminState?.activeSubTab,
    })
  );

  // Initialize filters without organization-related filters
  const [filters, setFilters] = useState({
    page: 1,
    per_page: 20,
    // Filter to show only condition-based prompts
    has_condition: true,
  });

  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [editingPromptId, setEditingPromptId] = useState(null);

  useEffect(() => {
    if (activeSubTab === 'Specialties') {
      dispatch(listLlms());
      dispatch(
        listPromptScreens(
          filters?.condition_id,
          filters?.specialty_id,
          'specialty'
        )
      );
      dispatch(getAllConditionListAction());
      dispatch(getAllSpecialtyListAction());
    }
  }, [dispatch, filters.condition_id, filters.specialty_id, activeSubTab]);

  useEffect(() => {
    // Only make API call when this tab is active
    if (activeSubTab === 'Specialties') {
      dispatch(listPromptsAction({ ...filters, scope: 'specialty' }));
    }
  }, [dispatch, filters, activeSubTab]);

  const handleEdit = (promptId) => {
    setIsEditModalVisible(true);
    setEditingPromptId(promptId);
  };

  const menuOptions = (id) => {
    return (
      <Menu
        onClick={({ key }) => {
          if (key === 'editPrompt') {
            handleEdit(id);
          }
        }}
      >
        <Menu.Item key="editPrompt">
          <div className="dropdown-text flex items-center space-x-2">
            <EditAppointmentIcon /> <span>Edit Prompt</span>
          </div>
        </Menu.Item>
      </Menu>
    );
  };

  const columns = [
    {
      title: <span className="font-[700]">Prompt</span>,
      dataIndex: 'prompt',
      key: 'prompt',
      render: (text) =>
        text && text.length > 50 ? `${text.substring(0, 50)}...` : text,
    },
    {
      title: <span className="font-[700]">Version</span>,
      dataIndex: 'version',
      key: 'version',
    },
    {
      title: <span className="font-[700]">LLM</span>,
      dataIndex: 'llm_id',
      key: 'llm_id',
      render: (llm_id, _record) => {
        const llmObj = llms?.llms?.find((llm) => llm.llm_id === llm_id);

        if (!llmObj) {
          return llm_id;
        }

        return getLlmDisplayComponent(llmObj);
      },
    },
    {
      title: <span className="font-[700]">Condition</span>,
      dataIndex: 'condition_name',
      key: 'condition_name',
    },
    {
      title: <span className="font-[700]">Specialty</span>,
      key: 'specialty_name',
      render: (text, record) => {
        const specialty = specialties?.find(
          (spec) => spec.specialty_id === record.specialty_id
        );
        return specialty?.specialty_name || 'N/A';
      },
    },
    {
      title: <span className="font-[700]">Prompt Screen</span>,
      dataIndex: 'prompt_screens_name',
      key: 'prompt_screens_name',
    },
    {
      title: 'Actions',
      key: 'actions',
      dataIndex: 'actions',
      render: (text, record) => {
        return (
          <Dropdown className="" overlay={menuOptions(record?.prompt_id)}>
            <a className="" onClick={(e) => e.preventDefault()}>
              <ActionMenuIcon />
            </a>
          </Dropdown>
        );
      },
    },
  ];

  const tableLoading =
    promptsLoading || llmsLoading || promptScreensLoading || conditionsLoading;
  const handleTableChange = (pagination) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: pagination.current,
      per_page: pagination.pageSize,
    }));
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row lg:justify-between justify-center items-center mb-4 gap-4">
        <div className="flex flex-col sm:flex-row flex-1 min-w-0">
          <ConditionsPromptsFilters filters={filters} setFilters={setFilters} />
        </div>
        <div className="flex justify-end items-end">
          <Button type={'button'} onClick={() => setIsCreateModalVisible(true)}>
            <PlusIcon className={`mr-2`} /> Add
          </Button>
        </div>
      </div>

      <div className="mt-5 ant-table rounded-xl shadow-xl">
        <SuperAdminTable
          className={'appointment-page-table'}
          data={prompts?.prompts}
          columns={columns}
          loading={tableLoading}
          rowKey="prompt_id"
          pagination={{
            total: prompts?.total_items,
            pageSize: prompts?.per_page,
            current: prompts?.page,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50'],
          }}
          onChange={handleTableChange}
        />
      </div>

      <EditPromptModal
        visible={isEditModalVisible}
        setVisible={setIsEditModalVisible}
        promptId={editingPromptId}
        filters={filters}
        selectedOrganizationUUID={null} // No org filter for conditions
        scope="specialty"
      />
      <CreatePromptModal
        visible={isCreateModalVisible}
        setVisible={setIsCreateModalVisible}
        filters={filters}
        selectedOrganizationUUID={null} // No org filter for conditions
        scope="specialty"
      />
    </div>
  );
};

export default ConditionsPrompts;
